// src/pages/appointments/index.jsx
import React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import useMyUpcomingAppointments from "@/hooks/useMyUpcomingAppointments";
import useMyPastAppointments from "@/hooks/useMyPastAppointments.js";
import useMyPendingAppointments from "@/hooks/useMyPendingAppointments.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { formatYearsExperienceLabel } from "@/utils/experience.js";
import {
  buildFavoriteDoctorIds,
  isFavoriteDoctor,
  setFavoriteDoctor,
  subscribeDoctorFavorites,
} from "@/services/doctorFavorites.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const ICONS = {
  empty: "/icons/appointments/date_range.svg", // 76x76
  years: "/icons/appointments/years_experience.svg",
  star: "/icons/appointments/star.svg",
  favorite: "/icons/appointments/favorite.svg",
  verified: "/icons/doctor/verified.svg",
  pendingVerification: "/icons/pending-verification.svg",
};

function safeTab(v) {
  return v === "upcoming" || v === "past" || v === "pending" ? v : "upcoming";
}

function parseAppointmentDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T00:00:00`)
    : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function monthKey(dateISO) {
  const date = parseAppointmentDate(dateISO);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function monthLabel(key, formatDate, t) {
  const [y, m] = String(key || "").split("-");
  const idx = Number(m) - 1;
  const year = Number(y);
  const month = Number(idx);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return t("appointments.past.common.monthFallback", "Month {year}", {
      year: y || "",
    }).trim();
  }
  const date = new Date(year, month, 1);
  if (Number.isNaN(date.getTime())) {
    return t("appointments.past.common.monthFallback", "Month {year}", {
      year: y || "",
    }).trim();
  }
  return formatDate(date, { month: "long", year: "numeric" });
}

function formatAppointmentLine(dateISO, timeHHMM, formatDate, t) {
  const d = parseAppointmentDate(dateISO);
  if (!d) return t("appointments.past.common.unknownDate", "—");
  const weekday = formatDate(d, { weekday: "long" });
  const day = formatDate(d, { day: "2-digit" });
  const month = formatDate(d, { month: "short" });
  return `${weekday} ${day} ${month} ${timeHHMM || ""}`.trim();
}

function groupByMonth(rows, formatDate, t) {
  const map = new Map();
  rows.forEach((row) => {
    const key = monthKey(row?.selectedDate || row?.when?.dateISO || "");
    if (!key) return;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });

  const keys = Array.from(map.keys()).sort((a, b) => (a < b ? -1 : 1));
  return keys.map((key) => ({
    key,
    label: monthLabel(key, formatDate, t),
    items: map.get(key),
  }));
}

function AppointmentRowCard({ row, nav }) {
  const { t } = useLanguage();
  const doctorName = resolvePatientFacingDoctorName(row?.doctor, row?.doctor?.name);
  const favoriteDoctorIds = buildFavoriteDoctorIds(row?.doctor);
  const isFavorite = isFavoriteDoctor(favoriteDoctorIds);
  const verificationStatus = normalizeVerificationStatus(
    row?.doctor?.verificationStatus || row?.doctor?.verified_status,
    row?.doctor?.verified ? "verified" : ""
  );
  const showVerifiedBadge =
    verificationStatus === "verified" || verificationStatus === "pending";
  const yearsExperienceLabel = formatYearsExperienceLabel(row?.doctor?.yearsExperience, t);

  return (
    <Card
      as="button"
      type="button"
      className="
        w-full text-left
        overflow-hidden
      "
      onClick={() =>
        nav(`/appointments/details/${row.id}`, {
          state: row,
        })
      }
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        nav(`/appointments/details/${row.id}`, {
          state: row,
        });
      }}
    >
      <div className="px-4 py-4 flex items-center gap-3">
        <AvatarThumb68
          src={row.doctor?.avatar || "/icons/doctor/avatar_placeholder.svg"}
          alt={doctorName}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="text-title-medium text-on-surface truncate">
                  {doctorName}
                </div>
                {showVerifiedBadge ? (
                  <span className="inline-flex h-5 w-5 shrink-0">
                    <img
                      src={
                        verificationStatus === "verified"
                          ? ICONS.verified
                          : ICONS.pendingVerification
                      }
                      alt=""
                      className="h-full w-full object-contain"
                      draggable="false"
                    />
                  </span>
                ) : null}
              </div>
              <div className="text-body-medium text-on-surface-var mt-0.5">
                {row.doctor?.specialty || "Doctor"}
              </div>

              <div className="mt-1 flex items-center gap-3 text-body-medium text-on-surface-var">
                <div className="flex items-center gap-1.5">
                  <span>{yearsExperienceLabel}</span>
                  <img
                    src={ICONS.years}
                    alt=""
                    className="h-[18px] w-[18px]"
                    draggable="false"
                  />
                </div>

                <div className="flex items-center gap-1.5">
                  <span>{row.doctor?.rating || "—"}</span>
                  <img
                    src={ICONS.star}
                    alt=""
                    className="h-6 w-6 translate-y-[2px]"
                    draggable="false"
                  />
                </div>
              </div>
            </div>

            <div className="shrink-0 pt-0.5">
              <button
                type="button"
                aria-label={
                  isFavorite
                    ? "Remove from favourites"
                    : "Add to favourites"
                }
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setFavoriteDoctor(favoriteDoctorIds, !isFavorite);
                }}
                className="h-11 w-11 grid place-items-center"
              >
                <img
                  src={
                    isFavorite
                      ? "/icons/patient/favorite.svg"
                      : "/icons/patient/favorite_outline.svg"
                  }
                  alt=""
                  className={isFavorite ? "h-6 w-6 opacity-100" : "h-6 w-6 opacity-70"}
                  draggable="false"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function AppointmentsIndex() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, formatDate } = useLanguage();

  const tab = safeTab(searchParams.get("tab") || "upcoming");

  const [moreOpen, setMoreOpen] = React.useState(false);

  // Real data (Upcoming)
  const { loading: upcomingLoading, error: upcomingError, items: upcomingItems } =
    useMyUpcomingAppointments();
  const { loading: pendingLoading, error: pendingError, items: pendingItems } =
    useMyPendingAppointments();
  const { items: pastItems } = useMyPastAppointments();

  if (tab === "past") {
    return <Navigate to="/appointments/past" replace />;
  }

  const upcomingGroups = React.useMemo(
    () => groupByMonth(upcomingItems || [], formatDate, t),
    [formatDate, t, upcomingItems]
  );
  const pastRows = React.useMemo(() => pastItems || [], [pastItems]);
  const pendingRows = React.useMemo(() => pendingItems || [], [pendingItems]);

  const counts = {
    upcoming: (upcomingItems?.length || 0),
    past: pastRows.length,
    pending: pendingRows.length,
  };

  // Toast (for delete success, etc.)
  const [toastMsg, setToastMsg] = React.useState("");
  const [, setFavoriteVersion] = React.useState(0);

  React.useEffect(() => subscribeDoctorFavorites(() => setFavoriteVersion((value) => value + 1)), []);

  React.useEffect(() => {
    if (!toastMsg) return;
    const t = window.setTimeout(() => setToastMsg(""), 3500);
    return () => window.clearTimeout(t);
  }, [toastMsg]);

  // Consume delete toast flag after navigation from Details/Delete
  React.useEffect(() => {
    const toast = searchParams.get("toast");
    if (toast !== "deleted") return;

    setToastMsg(t("appointments.index.toast.deleted", "Your appointment was deleted"));
    setSearchParams({}, { replace: true });

    try {
      sessionStorage.removeItem("afrimed:toast:appointments:deleted");
      localStorage.removeItem("afrimed:toast:appointments:deleted");
    } catch {}
  }, [searchParams, setSearchParams, t]);

  function setTab(next) {
    const t = safeTab(next);
    // Past has its own dedicated screen (search + month groups)
    if (t === "past") {
      nav("/appointments/past", { replace: false });
      return;
    }
    setSearchParams({ tab: t }, { replace: true });
  }

  const isEmpty =
    tab === "upcoming"
      ? !upcomingLoading && !upcomingError && (upcomingItems?.length || 0) === 0
      : tab === "pending"
        ? !pendingLoading && !pendingError && pendingRows.length === 0
        : false;
  
  const showUpcomingEmpty =
  tab === "upcoming" &&
  !upcomingLoading &&
  !upcomingError &&
  (upcomingItems?.length || 0) === 0;

  const emptyTitle =
    tab === "upcoming"
      ? t("appointments.index.upcomingEmpty.title", "No upcoming appointments")
      : tab === "pending"
        ? t(
            "appointments.index.pendingEmpty.title",
            "No pending appointments"
          )
        : t("appointments.index.empty.title", "No appointments to display");

  const emptyBody =
    tab === "upcoming"
      ? t(
          "appointments.index.upcomingEmpty.body",
          "You do not have any scheduled appointments yet.\nBook your next consultation to see it here."
        )
      : tab === "pending"
        ? t(
            "appointments.index.pendingEmpty.body",
            "Nothing is waiting for approval right now."
          )
        : t(
            "appointments.index.empty.body",
            "Your list is currently empty. Check back later!"
          );

  return (
  <div className="min-h-[100dvh] flex flex-col">
    <div className="afrimed-rail afrimed-rail--comfortable flex-1 flex flex-col pt-4">
      <header className="mb-3">
          <TopBarCenter
            title={t("appointments.index.title", "Appointments")}
            onBack={() => nav(-1)}
            right={
              <div className="relative">
                <button
                  type="button"
                  aria-label={t("appointments.index.more", "More")}
                  className="h-11 w-11 rounded-[12px] grid place-items-center"
                  onClick={() => setMoreOpen((v) => !v)}
                >
                  <img
                    src="/icons/appointments/more_vert.svg"
                    alt=""
                    className="h-6 w-6"
                    draggable="false"
                  />
                </button>

                {moreOpen ? (
                  <>
                    <button
                      type="button"
                      aria-label={t("common.close", "Close")}
                      className="fixed inset-0 z-[70] cursor-default"
                      onClick={() => setMoreOpen(false)}
                    />
                    <div
                      className="
                        absolute right-0 top-[48px]
                        z-[80]
                        w-[220px]
                        rounded-[12px]
                        bg-[rgb(var(--clr-surface-cont-highest))]
                        border border-[rgba(var(--clr-outline-variant),0.55)]
                        shadow-[0_16px_40px_rgba(2,6,23,0.14)]
                        overflow-hidden
                      "
                    >
                      <button
                        type="button"
                        className="
                          w-full text-left px-4 py-3
                          text-body-large text-on-surface
                          hover:bg-[rgba(2,6,23,0.04)]
                          active:bg-[rgba(2,6,23,0.06)]
                        "
                        onClick={() => {
                          setMoreOpen(false);
                          nav("/appointments/past");
                        }}
                      >
                        {t("appointments.index.pastAppointments", "Past appointments")}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            }
          />
        </header>

        {/* Pills row (single source of truth) */}
        <div className="mt-1 flex gap-3 overflow-x-auto no-scrollbar pb-1">
          <AfriPill
            variant="appointments"
            size="medium"
            value="upcoming"
            label={`${t("appointments.index.tabs.upcoming", "Upcoming")} (${counts.upcoming})`}
            isActive={tab === "upcoming"}
            onChange={setTab}
          />
          <AfriPill
            variant="appointments"
            size="medium"
            value="past"
            label={`${t("appointments.index.tabs.past", "Past")} (${counts.past})`}
            isActive={false /* Past lives on its own screen */}
            onChange={setTab}
          />
          <AfriPill
            variant="appointments"
            size="medium"
            value="pending"
            label={`${t("appointments.index.tabs.pending", "Pending for approval")} (${counts.pending})`}
            isActive={tab === "pending"}
            onChange={setTab}
          />
        </div>

        {/* Toast */}
        {toastMsg ? (
          <div
            className="
              fixed left-1/2 -translate-x-1/2
              bottom-[92px] md:bottom-6
              w-full max-w-[720px] px-4
              z-[80]
            "
          >
            <div
              className="
                w-full
                rounded-[10px]
                bg-[#0B0F1A]
                text-white
                px-4 py-3
                flex items-center justify-between gap-3
                shadow-[0_10px_30px_rgba(2,6,23,0.35)]
              "
              role="status"
              aria-live="polite"
            >
              <div className="text-body-medium truncate">{toastMsg}</div>
              <button
                type="button"
                onClick={() => setToastMsg("")}
                aria-label={t("common.close", "Close")}
                className="
                  shrink-0
                  h-9 w-9
                  rounded-[10px]
                  bg-[rgba(255,255,255,0.08)]
                  hover:bg-[rgba(255,255,255,0.12)]
                  active:bg-[rgba(255,255,255,0.12)]
                  flex items-center justify-center
                "
              >
                <span className="text-white text-[18px] leading-none translate-y-[-1px]">×</span>
              </button>
            </div>
          </div>
        ) : null}

              {/* Content */}
        {tab === "upcoming" ? (
          upcomingLoading ? (
            <Card className="mt-6 p-4">
              <div className="text-body-medium text-on-surface-var">
                {t("common.loading", "Loading...")}
              </div>
            </Card>
          ) : upcomingError || (upcomingItems?.length || 0) === 0 ? (
            <>
              {showUpcomingEmpty ? (
              <div className="mt-6">
                <FolaOnboardingCard role="patient" placement="patientAppointments" />
              </div>
              ) : null}

              {/* Center block */}
              <div className="flex-1 grid place-items-center text-center px-2">
                <div>
                  <img
                    src={ICONS.empty}
                    alt=""
                    className="h-[76px] w-[76px] mx-auto"
                    draggable="false"
                  />
                  <div className="mt-4 text-title-large text-on-surface">
                    {t("appointments.index.empty.title", "No appointments to display")}
                  </div>
                  <div className="mt-2 text-body-large text-on-surface-var max-w-[360px] mx-auto">
                    {t(
                      "appointments.index.empty.body",
                      "Your list is currently empty. Check back later!",
                    )}
                  </div>
                </div>
              </div>

              {/* Footer CTAs (NOT sticky, but premium width like onboarding) */}
              <div className="mt-auto pb-[calc(env(safe-area-inset-bottom)+16px)] md:pb-[calc(72px+16px)]">
                <div className="afrimed-rail">
                  <div className="w-full mx-auto max-w-[560px] px-3 md:px-0">
                    <div className="flex flex-col gap-3">
                      <Button size="lg" fullWidth onClick={() => nav("/appointments/doctors")}>
                        {t("appointments.index.bookNow", "Book now")}
                      </Button>

                      <Button
                        size="lg"
                        variant="ghost"
                        fullWidth
                        onClick={() => nav("/appointments/nearby")}
                      >
                        {t("appointments.index.nearby", "Healthcare near you")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="mt-6 space-y-10">
              {upcomingGroups.map((group) => (
                <div key={group.key}>
                  <div className="text-title-large text-on-surface text-center mb-6">
                    {group.label}
                  </div>

                  <div className="space-y-4">
                    {group.items.map((row) => (
                      <div key={row.id}>
                        <div className="text-title-medium text-on-surface-var mb-2">
                          {formatAppointmentLine(
                            row.selectedDate || row.when?.dateISO,
                            row.selectedTime || row.when?.time,
                            formatDate,
                            t,
                          )}
                        </div>

                        <Card
                          as="div"
                          role="button"
                          tabIndex={0}
                          className="
                            w-full text-left
                            overflow-hidden
                          "
                          onClick={() =>
                            nav(`/appointments/details/${row.id}`, {
                              state: row,
                            })
                          }
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            nav(`/appointments/details/${row.id}`, {
                              state: row,
                            });
                          }}
                        >
                          <div className="px-4 py-4 flex items-center gap-3">
                            <AvatarThumb68
                              src={row.doctor?.avatar || "/icons/doctor/avatar_placeholder.svg"}
                              alt={resolvePatientFacingDoctorName(row?.doctor, row?.doctor?.name)}
                            />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <div className="text-title-medium text-on-surface truncate">
                                      {resolvePatientFacingDoctorName(row?.doctor, row?.doctor?.name)}
                                    </div>
                                    {(() => {
                                      const verificationStatus = normalizeVerificationStatus(
                                        row?.doctor?.verificationStatus || row?.doctor?.verified_status,
                                        row?.doctor?.verified ? "verified" : ""
                                      );
                                      const showVerifiedBadge =
                                        verificationStatus === "verified" || verificationStatus === "pending";
                                      if (!showVerifiedBadge) return null;
                                      return (
                                        <span className="inline-flex h-5 w-5 shrink-0">
                                          <img
                                            src={
                                              verificationStatus === "verified"
                                                ? ICONS.verified
                                                : ICONS.pendingVerification
                                            }
                                            alt=""
                                            className="h-full w-full object-contain"
                                            draggable="false"
                                          />
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <div className="text-body-medium text-on-surface-var mt-0.5">
                                    {row.doctor?.specialty || "Doctor"}
                                  </div>

                                  <div className="mt-1 flex items-center gap-3 text-body-medium text-on-surface-var">
                                    <div className="flex items-center gap-1.5">
                                      <span>{row.doctor?.yearsExperience || "—"}</span>
                                      <img
                                        src={ICONS.years}
                                        alt=""
                                        className="h-[18px] w-[18px]"
                                        draggable="false"
                                      />
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                      <span>{row.doctor?.rating || "—"}</span>
                                      <img
                                        src={ICONS.star}
                                        alt=""
                                        className="h-6 w-6 translate-y-[2px]"
                                        draggable="false"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="shrink-0 pt-0.5">
                                  {(() => {
                                    const favoriteDoctorIds = buildFavoriteDoctorIds(row?.doctor);
                                    const isFavorite = isFavoriteDoctor(favoriteDoctorIds);
                                    return (
                                  <button
                                    type="button"
                                    aria-label={
                                      isFavorite
                                        ? "Remove from favourites"
                                        : "Add to favourites"
                                    }
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setFavoriteDoctor(favoriteDoctorIds, !isFavorite);
                                    }}
                                    className="h-11 w-11 grid place-items-center"
                                  >
                                    <img
                                      src={
                                        isFavorite
                                          ? "/icons/patient/favorite.svg"
                                          : "/icons/patient/favorite_outline.svg"
                                      }
                                      alt=""
                                      className={isFavorite ? "h-6 w-6 opacity-100" : "h-6 w-6 opacity-70"}
                                      draggable="false"
                                    />
                                  </button>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : tab === "pending" ? (
          pendingLoading ? (
            <Card className="mt-6 p-4">
              <div className="text-body-medium text-on-surface-var">
                {t("common.loading", "Loading...")}
              </div>
            </Card>
          ) : pendingError ? (
            <Card className="mt-6 p-4">
              <div className="text-body-medium text-on-surface-var">
                {pendingError}
              </div>
            </Card>
          ) : isEmpty ? (
            <>
              <div className="flex-1 grid place-items-center text-center px-2">
                <div>
                  <img
                    src={ICONS.empty}
                    alt=""
                    className="h-[76px] w-[76px] mx-auto"
                    draggable="false"
                  />
                  <div className="mt-4 text-title-large text-on-surface">
                    {emptyTitle}
                  </div>
                  <div className="mt-2 text-body-large text-on-surface-var max-w-[420px] mx-auto whitespace-pre-line">
                    {emptyBody}
                  </div>
                </div>
              </div>

              <div className="mt-auto pb-[calc(env(safe-area-inset-bottom)+16px)] md:pb-[calc(72px+16px)]">
                <Button fullWidth onClick={() => nav("/appointments/doctors")}>
                  {t("appointments.index.bookNow", "Book now")}
                </Button>
              </div>
            </>
          ) : (
            <div className="mt-6 space-y-4">
              {pendingRows.map((p) => (
                <div key={p.id}>
                  <div className="text-title-medium text-on-surface-var mb-2">
                    {formatAppointmentLine(
                      p.selectedDate || p.when?.dateISO,
                      p.selectedTime || p.when?.time,
                      formatDate,
                      t
                    )}
                  </div>
                  <AppointmentRowCard row={p} nav={nav} />
                </div>
              ))}
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
